import type {
  CapabilityType,
  Entitlements,
} from '../../access/models/Entitlements';
import type { ResolvedFeatureFlags } from '../../feature-flags/models/FeatureFlags';

export interface AtlasWorkloadPlanningPolicy {
  readonly canOpenSurface: boolean;
  readonly canViewAdvancedAnalytics: boolean;
  readonly canUseScenarioDrafts: boolean;
  readonly canUseTimeTravel: boolean;
  readonly canExportScenario: boolean;
  readonly canApplyScenario: boolean;
  readonly partnerPlanningEnabled: boolean;
  readonly reason?: string;
}

export function resolveAtlasWorkloadPlanningPolicy(
  entitlements: Partial<Entitlements> | undefined,
  flags: Partial<ResolvedFeatureFlags> | undefined,
): AtlasWorkloadPlanningPolicy {
  const partnerPlanningEnabled = flags?.flags?.ATLAS_WORKLOAD_PLANNING ?? false;
  const hasBasicAtlas = hasCapability(entitlements, 'atlas.view.structural');
  const advancedGrant = partnerPlanningEnabled ||
    hasCapability(entitlements, 'atlas.view.analytic') ||
    hasAddOn(entitlements, 'atlas');
  // The pilot flag is a temporary view + draft grant only. Applying a
  // scenario to the org model or touching sensitivity-tiered data always
  // requires the real capabilities, matching server-side entitlement.
  const scenarioDraftGrant = partnerPlanningEnabled ||
    hasCapability(entitlements, 'atlas.scenario.model');
  const scenarioApplyGrant = hasCapability(entitlements, 'atlas.scenario.model');
  const timeTravelGrant = partnerPlanningEnabled ||
    hasCapability(entitlements, 'atlas.timetravel');
  const sensitivityGrant = hasCapability(entitlements, 'atlas.sensitivity.tiers');

  return {
    canOpenSurface: hasBasicAtlas,
    canViewAdvancedAnalytics: hasBasicAtlas && advancedGrant,
    canUseScenarioDrafts: hasBasicAtlas && scenarioDraftGrant,
    canUseTimeTravel: hasBasicAtlas && timeTravelGrant,
    canExportScenario: hasBasicAtlas && scenarioApplyGrant && sensitivityGrant,
    canApplyScenario: hasBasicAtlas && scenarioApplyGrant && sensitivityGrant,
    partnerPlanningEnabled,
    ...(hasBasicAtlas ? {} : { reason: 'Atlas structural view is not enabled for this workspace.' }),
  };
}

function hasCapability(
  entitlements: Partial<Entitlements> | undefined,
  capability: CapabilityType,
): boolean {
  const capabilities = entitlements?.capabilities?.capabilities;
  return Array.isArray(capabilities) && capabilities.includes(capability);
}

function hasAddOn(
  entitlements: Partial<Entitlements> | undefined,
  addOn: NonNullable<Entitlements['addOns']>[number],
): boolean {
  return Array.isArray(entitlements?.addOns) && entitlements.addOns.includes(addOn);
}
